import { useEffect, useMemo } from 'react'
import {Star, TrendingUp, Users, AlertCircle, BarChart2, Award, ThumbsUp} from 'lucide-react'
import EmpresaLayout from '@/components/EmpresaLayout'
import { useAuth } from '@/hooks/useAuth'
import { useEvaluations } from '@/hooks/useEvaluations'
import { COMPANY_EVALUATION_ASPECTS } from '@/lib/evaluationAspects'

const BAR_COLORS = [
  'bg-amber-400', 'bg-blue-400', 'bg-emerald-400', 'bg-violet-400',
  'bg-rose-400', 'bg-teal-400', 'bg-orange-400',
]

function scoreColor(v: number) {
  if (v >= 8) return 'text-emerald-600'
  if (v >= 6) return 'text-amber-600'
  return 'text-red-500'
}

function reputationLabel(avg: number) {
  if (avg >= 9) return { label: 'Excelente empleador', color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' }
  if (avg >= 7.5) return { label: 'Muy buen empleador', color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-100' }
  if (avg >= 6) return { label: 'Buen empleador', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' }
  if (avg >= 4) return { label: 'Con áreas de mejora', color: 'text-orange-700', bg: 'bg-orange-50 border-orange-200' }
  return { label: 'Requiere atención', color: 'text-red-700', bg: 'bg-red-50 border-red-200' }
}

function EmpresaReputacion() {
  const { user } = useAuth()
  const { evaluations, loading, fetchCompanyReviews } = useEvaluations()

  useEffect(() => {
    if (user?.userId) fetchCompanyReviews(user.userId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId])

  const globalAvg = useMemo(() => {
    if (evaluations.length === 0) return 0
    const sum = evaluations.reduce((acc, e) => acc + (e.average || 0), 0)
    return Math.round((sum / evaluations.length) * 10) / 10
  }, [evaluations])

  const aspectAverages = useMemo(() => {
    if (evaluations.length === 0) return {}
    const result: Record<string, number> = {}
    COMPANY_EVALUATION_ASPECTS.forEach((asp) => {
      const vals = evaluations.map((e) => Number(e.scores?.[asp.key]) || 0)
      result[asp.key] = Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 10) / 10
    })
    return result
  }, [evaluations])

  const sortedAspects = useMemo(() => {
    return [...COMPANY_EVALUATION_ASPECTS]
      .map((asp) => ({ ...asp, avg: aspectAverages[asp.key] ?? 0 }))
      .sort((a, b) => b.avg - a.avg)
  }, [aspectAverages])

  const weakAspects = useMemo(() => {
    return [...sortedAspects].sort((a, b) => a.avg - b.avg).slice(0, 3)
  }, [sortedAspects])

  const rep = reputationLabel(globalAvg)

  return (
    <EmpresaLayout
      title="Mi reputación como empleador"
      subtitle="Calificaciones que tus colaboradores han dado a tu empresa. Usa estos datos para mejorar."
    >
      {loading ? (
        <div className="flex items-center gap-3 py-20 text-center text-sm text-slate-400 justify-center">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-amber-400 border-t-transparent" />
          Cargando evaluaciones de colaboradores...
        </div>
      ) : evaluations.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white py-20 text-center">
          <ThumbsUp size={40} className="mx-auto text-slate-300" />
          <p className="mt-4 text-sm font-semibold text-slate-700">Aún no tienes evaluaciones de colaboradores</p>
          <p className="mt-1 max-w-sm mx-auto text-sm text-slate-500">
            Cuando tus colaboradores vinculados te evalúen desde Beeznz Work, los resultados aparecerán aquí para ayudarte a mejorar como empleador.
          </p>
        </div>
      ) : (
        <div className="space-y-6">

          {/* Reputación global */}
          <div className={`flex flex-wrap items-center justify-between gap-4 rounded-2xl border p-6 ${rep.bg}`}>
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-sm">
                <Award size={28} className="text-amber-500" />
              </div>
              <div>
                <p className={`text-xl font-bold ${rep.color}`}>{rep.label}</p>
                <p className="text-xs text-slate-500 mt-0.5">Basado en {evaluations.length} evaluación{evaluations.length !== 1 ? 'es' : ''} de colaboradores</p>
              </div>
            </div>
            <div className="text-right">
              <p className={`text-5xl font-black ${scoreColor(globalAvg)}`}>{globalAvg.toFixed(1)}</p>
              <p className="text-xs text-slate-400">promedio general / 10</p>
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {[
              { label: 'Evaluaciones recibidas', value: evaluations.length, icon: BarChart2, color: 'text-blue-600', bg: 'bg-blue-50' },
              { label: 'Promedio general', value: `${globalAvg.toFixed(1)}/10`, icon: Star, color: scoreColor(globalAvg), bg: globalAvg >= 8 ? 'bg-emerald-50' : globalAvg >= 6 ? 'bg-amber-50' : 'bg-red-50' },
              { label: 'Mejor aspecto', value: sortedAspects[0]?.label || '—', icon: TrendingUp, color: 'text-emerald-600', bg: 'bg-emerald-50' },
              { label: 'Colaboradores únicos', value: new Set(evaluations.map((e) => e.collaboratorUserId)).size, icon: Users, color: 'text-violet-600', bg: 'bg-violet-50' },
            ].map(({ label, value, icon: Icon, color, bg }) => (
              <div key={label} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${bg}`}>
                  <Icon size={18} className={color} />
                </div>
                <p className={`mt-3 text-xl font-bold leading-tight ${color}`}>{value}</p>
                <p className="mt-0.5 text-xs text-slate-500">{label}</p>
              </div>
            ))}
          </div>

          {/* Barras por aspecto */}
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="mb-5 text-sm font-semibold text-slate-900">Promedio por aspecto</h2>
            <div className="space-y-3">
              {sortedAspects.map((asp, i) => (
                <div key={asp.key}>
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-xs text-slate-600">{asp.label}</span>
                    <span className={`text-xs font-bold ${scoreColor(asp.avg)}`}>{asp.avg.toFixed(1)}</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                    <div
                      className={`h-2 rounded-full transition-all ${BAR_COLORS[i % BAR_COLORS.length]}`}
                      style={{ width: `${(asp.avg / 10) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Áreas de oportunidad */}
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
            <div className="mb-3 flex items-center gap-2">
              <AlertCircle size={16} className="text-amber-600" />
              <h2 className="text-sm font-semibold text-amber-900">Áreas de oportunidad</h2>
            </div>
            <div className="space-y-2">
              {weakAspects.map((asp) => (
                <div key={asp.key} className="flex items-center justify-between rounded-lg bg-white px-3 py-2.5">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{asp.label}</p>
                    <p className="text-xs text-slate-400">{asp.description}</p>
                  </div>
                  <span className={`ml-4 shrink-0 text-sm font-bold ${scoreColor(asp.avg)}`}>{asp.avg.toFixed(1)}</span>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-amber-700">
              Estos aspectos tienen la calificación más baja por parte de tus colaboradores. Mejorarlos fortalecerá tu reputación en Beeznz Work.
            </p>
          </div>

          {/* Historial de evaluaciones individuales */}
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="mb-4 text-sm font-semibold text-slate-900">Historial de evaluaciones recibidas</h2>
            <div className="space-y-3">
              {evaluations.map((ev) => (
                <div key={ev._id} className="rounded-xl border border-slate-100 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">
                        {ev.collaboratorName || 'Colaborador'}
                      </p>
                      <p className="text-xs text-slate-400">Periodo: {ev.period}</p>
                    </div>
                    <span className={`text-xl font-black ${scoreColor(ev.average)}`}>
                      {ev.average.toFixed(1)}
                    </span>
                  </div>
                  {ev.comments && (
                    <p className="mt-2 text-xs text-slate-600 border-t border-slate-100 pt-2">
                      <span className="font-medium">Comentario:</span> {ev.comments}
                    </p>
                  )}
                  {ev.observations && (
                    <p className="mt-1 text-xs text-slate-600">
                      <span className="font-medium">Área de mejora:</span> {ev.observations}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

        </div>
      )}
    </EmpresaLayout>
  )
}

export default EmpresaReputacion
